import type React from 'react';
import { styled } from 'styled-components';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef/index.ts';

import { InfoTooltipIcon, type InfoTooltipIconProps } from '../../Feedback/InfoTooltipIcon/index.tsx';

export interface LabelProps extends Omit<React.HTMLProps<HTMLLabelElement>, 'as'> {
  tooltip?: InfoTooltipIconProps['tooltip'];
  tooltipType?: InfoTooltipIconProps['type'];
  noMargin?: boolean;
  dataTestId?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  as?: string | React.ComponentType<any>;
}

export const StyledLabel = styled.label<{ disabled: boolean; $noMargin: boolean }>`
  display: flex;
  align-items: center;
  ${({ theme }) => theme.typography.textMedium};
  color: ${({ disabled, theme }) => (disabled ? theme.colors.grey70_1 : theme.colors.grey100_1)};
  margin-bottom: ${({ $noMargin }) => ($noMargin ? '0px' : '4px')};
  gap: 4px;
`;

const RequiredSpan = styled.span<{ disabled?: boolean }>`
  color: ${({ theme, disabled }) => (disabled ? theme.colors.critical30 : theme.colors.critical100)};

  &::before {
    padding-left: 2px;
    content: '*';
  }
`;

const combineTestIds = (element: string, root?: string): string | undefined => (root ? `${root}-${element}` : undefined);

const Label: React.FC<LabelProps> = ({ children, required, tooltip, tooltipType, disabled, noMargin, className, dataTestId, as }) => {
  const getDataTestId = useCallbackRef((elementId: string): string | undefined => combineTestIds(elementId, dataTestId));

  return (
    <StyledLabel as={as} disabled={disabled} $noMargin={noMargin} className={className} data-testid={dataTestId}>
      <span>
        {children}
        {required ? <RequiredSpan data-testid={getDataTestId('required-indicator')} disabled={disabled} /> : ''}
      </span>
      {tooltip ? <InfoTooltipIcon tooltip={tooltip} type={tooltipType} dataTestIdIcon={getDataTestId('info-icon')} /> : ''}
    </StyledLabel>
  );
};

export default Label;
